import { Outlet, Route, Routes } from 'react-router'
import { NavLink, NavLinkRenderProps } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { ACCOUNT_PERMISSION } from '../shared/constants'
import { useHasAccountPermission } from './account-permissions'
import { useObjectList } from './api/object'
import { ButtonLink } from './components/button'
import { Icon } from './components/icon'
import addressBookIcon from './icons/address-book.svg'
import dashboardIcon from './icons/dashboard.svg'
import downloadIcon from './icons/download.svg'
import expenseIcon from './icons/expense.svg'
import homeIcon from './icons/home.svg'
import logoIcon from './icons/logo.svg'
import pinIcon from './icons/pin.svg'
import proposalIcon from './icons/proposal.svg'
import proposalvehicleIcon from './icons/proposalvehicle.svg'
import reportingIcon from './icons/reporting.svg'
import reservationIcon from './icons/reservation.svg'
import userIcon from './icons/user.svg'
import vehicleIcon from './icons/vehicle.svg'

const navLinkClassName = ({ isActive }: NavLinkRenderProps) => {
	const base = 'flex rounded-xl px-4 py-3 hover:bg-gray-100 group gap-4 items-center'

	if (isActive) {
		return `${base} bg-blue-50 text-indigo-600 font-medium `
	}
	return base
}

const iconClassName =
	'w-5 h-5 transform transition-transform duration-200 md:group-hover:scale-105 origin-center'

export function FrontRoot() {
	const canAccessReporting = useHasAccountPermission(ACCOUNT_PERMISSION.REPORTING)
	const { data: newProposalsData } = useObjectList(
		'proposal',
		{
			status: ['new'],
			page: 1,
			orderKey: 'id',
			orderDir: 'DESC',
		},
		{
			// Počet nezpracovaných poptávek obnovuj každých 60 s (i na pozadí) a při
			// návratu do okna, aby badge zachytil i poptávky příchozí zvenčí.
			refetchInterval: 60_000,
			refetchIntervalInBackground: true,
			refetchOnWindowFocus: true,
		},
	)
	const newCount = newProposalsData?.total ?? 0

	return (
		<>
			<li>
				<NavLink to="." end className={navLinkClassName}>
					<Icon src={homeIcon} className={iconClassName} />
					Dashboard
				</NavLink>
			</li>
			<li>
				<NavLink to="dashboard" className={navLinkClassName}>
					<Icon src={dashboardIcon} className={iconClassName} />
					Grafika
				</NavLink>
			</li>
			{canAccessReporting && (
				<li>
					<NavLink to="reporting" className={navLinkClassName}>
						<Icon src={reportingIcon} className={iconClassName} />
						Statistiky
					</NavLink>
				</li>
			)}
			<li>
				<NavLink to="reservation" className={navLinkClassName}>
					<Icon src={reservationIcon} className={iconClassName} />
					Rezervace
				</NavLink>
			</li>
			<li>
				<NavLink to="proposal" className={navLinkClassName}>
					<div className="flex items-center gap-4">
						<Icon src={proposalIcon} className={iconClassName} />
						<span>Poptávky</span>
					</div>
					{newCount > 0 && (
						<span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-md min-w-[20px] text-center">
							{newCount > 99 ? '99+' : newCount}
						</span>
					)}
				</NavLink>
			</li>
			<li>
				<NavLink to="proposal_vehicle" className={navLinkClassName}>
					<Icon src={proposalvehicleIcon} className={iconClassName} />
					Vozidla poptávek
				</NavLink>
			</li>
			<li>
				<NavLink to="client" className={navLinkClassName}>
					<Icon src={addressBookIcon} className={iconClassName} />
					Adresář
				</NavLink>
			</li>
			<li>
				<NavLink to="vehicle" className={navLinkClassName}>
					<Icon src={vehicleIcon} className={iconClassName} />
					Vozidla
				</NavLink>
			</li>
			<li>
				<NavLink to="location" className={navLinkClassName}>
					<Icon src={pinIcon} className={iconClassName} />
					Pobočky
				</NavLink>
			</li>
			<li>
				<NavLink to="brand" className={navLinkClassName}>
					<Icon src={userIcon} className={iconClassName} />
					Pronajímatelé
				</NavLink>
			</li>
			<li>
				<NavLink to="payment" className={navLinkClassName}>
					<Icon src={downloadIcon} className={iconClassName} />
					Přijaté příjmy
				</NavLink>
			</li>
			<li>
				<NavLink to="expense" className={navLinkClassName}>
					<Icon src={expenseIcon} className={iconClassName} />
					Plánované příjmy
				</NavLink>
			</li>
		</>
	)
}

export function BackRoot() {
	return (
		<>
			<li>
				<NavLink to="me" className={navLinkClassName}>
					Osobní
				</NavLink>
			</li>
			<li>
				<NavLink to="users" className={navLinkClassName}>
					Uživatelé
				</NavLink>
			</li>
			<li>
				<NavLink to="vehicle" className={navLinkClassName}>
					Vozidla
				</NavLink>
			</li>
			<li>
				<NavLink to="location" className={navLinkClassName}>
					Pobočky
				</NavLink>
			</li>
			<li>
				<NavLink to="brand" className={navLinkClassName}>
					Pronajímatelé
				</NavLink>
			</li>
			<li>
				<NavLink to="reservation" className={navLinkClassName}>
					Rezervace
				</NavLink>
			</li>
			<li>
				<NavLink to="proposal" className={navLinkClassName}>
					Poptávky
				</NavLink>
			</li>
			<li>
				<NavLink to="proposal_vehicle" className={navLinkClassName}>
					Vozidla poptávek
				</NavLink>
			</li>
			<li>
				<NavLink to="proposal-item" className={navLinkClassName}>
					Položky poptávek
				</NavLink>
			</li>
			<li>
				<NavLink to="client" className={navLinkClassName}>
					Adresář
				</NavLink>
			</li>
			<li>
				<NavLink to="payment" className={navLinkClassName}>
					Přijaté příjmy
				</NavLink>
			</li>
			<li>
				<NavLink to="expense" className={navLinkClassName}>
					Plánované příjmy
				</NavLink>
			</li>
			<li>
				<NavLink to="roles" className={navLinkClassName}>
					Oprávnění
				</NavLink>
			</li>
		</>
	)
}

export function Root() {
	return (
		<>
			<div className="flex h-screen screen:overflow-hidden print:overflow-visible">
				<aside className="fixed top-0 left-0 z-9999 flex h-screen w-[260px] flex-col overflow-y-auto border-r border-gray-200 bg-white transition-all duration-300 lg:static lg:translate-x-0 -translate-x-full print:hidden">
					<div>
						<div className="px-6 py-8">
							<Icon src={logoIcon} width="103.08" height="32" />
						</div>
						<div className="px-4 pb-3">
							<ButtonLink
								variant="SECONDARY"
								to="svc/reservation/create"
								className="w-full text-center"
							>
								Vytvořit rezervaci
							</ButtonLink>
						</div>
					</div>
					<div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
						<nav className="p-4 text-sm">
							<ul className="space-y-3 mb-3 flex-1">
								<Routes>
									<Route path="svc/*" element={<FrontRoot />} />
									<Route path="stp/*" element={<BackRoot />} />
								</Routes>
							</ul>
						</nav>
					</div>
					<div className="py-2 px-4 mt-auto flex justify-between">
						<NavLink to="/svc" className={navLinkClassName}>
							SVC
						</NavLink>
						<NavLink to="/stp" className={navLinkClassName}>
							STP
						</NavLink>
					</div>
				</aside>
				<div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden print:overflow-visible">
					<div className="p-6 md:p-8">
						<Outlet />
					</div>
				</div>
			</div>
			<ToastContainer
				position="bottom-left"
				className="print:hidden"
				closeOnClick
				newestOnTop
			/>
		</>
	)
}
